/**
 * Enhanced Sulfadimethoxine Dosage Calculator
 *
 * Multi-species dosage calculator with improved UX and professional medical interface.
 * Integrates with trust signal system and provides comprehensive dosing guidance.
 */

import Alpine from 'alpinejs';
import {
  calculateDosage,
  convertWeight,
  validateDosageRange,
  generateDosageSchedule,
  getSpeciesEmoji,
  getSpeciesName,
  DOSAGE_PROTOCOLS,
  type SpeciesType
} from '../utils/medication-calculations';

// Use existing Alpine instance if available, otherwise use imported one
const AlpineInstance = (window as any).Alpine || Alpine;
if (!(window as any).Alpine) {
  (window as any).Alpine = Alpine;
}

// Register component on the Alpine instance
AlpineInstance.data('enhancedDosageCalculator', () => ({
    // Form inputs
    species: 'dog' as SpeciesType,
    weight: '',
    weightUnit: 'lbs' as 'kg' | 'lbs',
    formulation: 'tablet' as 'tablet' | 'suspension',
    doseType: 'loading' as 'loading' | 'maintenance',

    // Calculation results
    result: null as { day1: string; maintenance: string; duration: string; notes: string[] } | null,
    weightKg: 0,
    weightLbs: 0,
    validation: null as { valid: boolean; message: string; severity: string } | null,
    warnings: [] as string[],

    // UI state
    showResults: false,
    showAdvancedOptions: false,

    init() {
      // Watch for changes and recalculate
      this.$watch('weight', () => this.calculate());
      this.$watch('weightUnit', () => this.calculate());
      this.$watch('species', () => this.calculate());
      this.$watch('formulation', () => this.calculate());
      this.$watch('doseType', () => this.calculate());
    },

    get speciesOptions(): SpeciesType[] {
      return ['dog', 'cat', 'cattle', 'swine', 'sheep', 'poultry', 'rabbit', 'horse'];
    },

    get speciesName(): string {
      return getSpeciesName(this.species);
    },

    get speciesEmoji(): string {
      return getSpeciesEmoji(this.species);
    },

    get protocol() {
      return DOSAGE_PROTOCOLS[this.species];
    },

    get isFoodAnimal(): boolean {
      return ['cattle', 'swine', 'sheep', 'poultry'].includes(this.species);
    },

    get requiresExtraLabel(): boolean {
      return this.protocol.extraLabel === true;
    },

    validate(): boolean {
      this.warnings = [];

      // Check weight is entered
      const weightNum = parseFloat(this.weight);
      if (!this.weight || isNaN(weightNum) || weightNum <= 0) {
        this.warnings.push('Please enter a valid weight greater than 0');
        return false;
      }

      // Convert weight
      const converted = convertWeight(weightNum, this.weightUnit);
      this.weightKg = converted.kg;
      this.weightLbs = converted.lbs;

      // Validate weight range
      const protocol = this.protocol;
      if (this.weightKg < protocol.minWeight) {
        this.warnings.push(
          `Weight is below minimum for ${protocol.name} (${protocol.minWeight}kg / ${Math.round(protocol.minWeight * 2.2)}lbs). Please verify the weight is correct.`
        );
        return false;
      }

      if (this.weightKg > protocol.maxWeight) {
        this.warnings.push(
          `Weight exceeds typical maximum for ${protocol.name} (${protocol.maxWeight}kg / ${Math.round(protocol.maxWeight * 2.2)}lbs). Please verify the weight is correct.`
        );
      }

      // Additional warnings
      if (this.species === 'cat' && this.weightKg < 2) {
        this.warnings.push('Very small cat - use extra care with dosing and monitor closely');
      }

      if (this.species === 'dog' && this.weightKg < 2) {
        this.warnings.push('Very small dog - consider consulting veterinarian for precise dosing');
      }

      if (this.isFoodAnimal) {
        this.warnings.push('IMPORTANT: Observe withdrawal times for meat/milk/eggs before human consumption');
      }

      if (this.requiresExtraLabel) {
        this.warnings.push('Extra-label use - requires veterinary prescription and supervision');
      }

      return true;
    },

    calculate() {
      if (!this.validate()) {
        this.result = null;
        this.showResults = false;
        this.validation = null;
        return;
      }

      // Perform validation
      const dosePerKg = this.doseType === 'loading' ? this.protocol.loading : this.protocol.maintenance;
      this.validation = validateDosageRange(this.weightKg, this.species, dosePerKg);

      // Generate dosage schedule
      this.result = generateDosageSchedule(
        this.species,
        this.weightKg,
        this.formulation as 'tablet' | 'suspension'
      );

      this.showResults = true;
    },

    reset() {
      this.weight = '';
      this.result = null;
      this.showResults = false;
      this.warnings = [];
      this.validation = null;
    },

    printResults() {
      window.print();
    },

    get hasResults(): boolean {
      return this.showResults && this.result !== null;
    },

    get weightDisplay(): string {
      if (!this.weight) return '';
      return `${this.weightKg}kg / ${this.weightLbs}lbs`;
    },

    // Helper methods for template access
    getEmojiFor(species: SpeciesType): string {
      return getSpeciesEmoji(species);
    },

    getNameFor(species: SpeciesType): string {
      return getSpeciesName(species);
    }
  }));

Alpine.start();
